import { ComponentProps, useEffect, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { DetailsItemsList, NumberInput } from '@factorialco/f0-react/dist/experimental'
import { UpdateQuoteDiscountDocument } from 'generated/resources/graphql'
import debounce from 'lodash/debounce'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

interface QuotePricingFinancialSummaryProps {
  totalHours: number
  baseCostCents: number
  baseAmountCents: number
  totalAmountCents: number
  taxesTotalAmountCents: number
  totalProfitPercentage: number
  totalProfitCents: number
  currency: string
  locale: string
  isProductQuote: boolean
  isDraftStatus: boolean
  discountPercentage: number | null | undefined
  quoteId: string
}
const QuotePricingFinancialSummary = ({
  totalHours,
  baseCostCents,
  baseAmountCents,
  taxesTotalAmountCents,
  totalAmountCents,
  totalProfitPercentage,
  totalProfitCents,
  currency,
  locale,
  isProductQuote,
  isDraftStatus,
  discountPercentage,
  quoteId,
}: QuotePricingFinancialSummaryProps) => {
  const [inputDiscount, setInputDiscount] = useState<number | null>(discountPercentage ?? null)
  useEffect(() => {
    setInputDiscount(discountPercentage ?? null)
  }, [discountPercentage])

  const [updateDiscount] = useMutation(UpdateQuoteDiscountDocument, {
    extractorFn: (data) => data.financeMutations.updateDiscountFinanceQuote,
  })

  const debouncedUpdateDiscount = useMemo(
    () =>
      debounce(async (value: number | null) => {
        const result = await updateDiscount({
          variables: {
            id: quoteId,
            discountPercentage: value,
          },
        })
        handleResult(result, () => undefined)
      }, 700),
    [updateDiscount, quoteId]
  )
  useEffect(() => () => debouncedUpdateDiscount.cancel(), [debouncedUpdateDiscount])

  const handleDiscountChange = (value: number | null) => {
    setInputDiscount(value)
    debouncedUpdateDiscount(value)
  }

  const profitAlertDescription = () => {
    const totalProfit = MoneyWithCurrency({
      optionalDecimals: true,
      amount: Number(totalProfitCents),
      currency: currency,
      locale,
    })
    return `${totalProfit} (${totalProfitPercentage}%)`
  }

  const buildDetailItemMoneyContent = (
    amountCents: number
  ): ComponentProps<typeof DetailsItemsList>['details'][number]['content'] => {
    return {
      type: 'item',
      text: MoneyWithCurrency({
        optionalDecimals: true,
        amount: amountCents,
        currency: currency,
        locale,
      }).toString(),
    }
  }

  const hoursAndCostsDetails: ComponentProps<typeof DetailsItemsList>['details'] = [
    ...(!isProductQuote
      ? [
          {
            title: i18n.t('finance.quotes.quote_phases_table.sumary.total_hours'),
            content: {
              type: 'item',
              text: totalHours + 'h',
            },
          },
          {
            title: i18n.t('finance.quotes.quote_phases_table.sumary.estimated_costs'),
            content: buildDetailItemMoneyContent(baseCostCents),
          },
        ]
      : []),

    {
      title: i18n.t('finance.quotes.quote_phases_table.sumary.subtotal'),
      content: buildDetailItemMoneyContent(baseAmountCents),
    },
  ]

  const discountAmountCents = Math.round(baseAmountCents * ((discountPercentage ?? 0) / 100))
  const discountDetails: ComponentProps<typeof DetailsItemsList>['details'] = [
    {
      title: i18n.t('finance.quotes.form.discount_section'),
      content: {
        type: 'item',
        text:
          discountPercentage && discountPercentage > 0
            ? `${MoneyWithCurrency({ optionalDecimals: true, amount: discountAmountCents, currency, locale })}`
            : '-',
      },
    },
  ]

  const totalDetails: ComponentProps<typeof DetailsItemsList>['details'] = [
    ...(isProductQuote
      ? [
          {
            title: i18n.t('finance.quotes.quote_phases_table.sumary.taxes'),
            content: buildDetailItemMoneyContent(taxesTotalAmountCents),
          },
        ]
      : []),
    {
      title: i18n.t('finance.quotes.quote_phases_table.sumary.total'),
      content: buildDetailItemMoneyContent(totalAmountCents),
    },
  ]

  return (
    <Box flexDirection="column" gap="s12">
      <DetailsItemsList
        details={hoursAndCostsDetails}
        tableView
        title={i18n.t('finance.quotes.quote_phases_table.sumary.title')}
      />
      <DetailsItemsList details={discountDetails} tableView />
      <Box flexDirection="row" gap="s8" alignItems="stretch">
        <Box style={{ flexGrow: '1' }}>
          <NumberInput
            label={i18n.t('finance.quotes.form.discount_section')}
            hideLabel
            size="md"
            placeholder={i18n.t('finance.quotes.quote_phases_table.sumary.discount_placeholder')}
            units="%"
            maxDecimals={2}
            max={100}
            min={0}
            hint=""
            locale={locale}
            value={inputDiscount}
            onChange={handleDiscountChange}
            disabled={!isDraftStatus}
            clearable
          />
        </Box>
      </Box>
      <DetailsItemsList details={totalDetails} tableView />
      <F0Alert
        title={i18n.t('finance.quotes.quote_phases_table.sumary.profit_margin')}
        description={profitAlertDescription()}
        variant="info"
      />
    </Box>
  )
}

export default QuotePricingFinancialSummary
